/**
 * API client configuration
 */

import axios from "axios";
import toast from "react-hot-toast";
import type { ApartmentListParams } from "./types";
import {
  notifyAuthCleared,
  notifyBackendUnavailable,
  redirectToLoginAfterAuthFailure,
  refreshTokens,
} from "./authRefresh";
import { userIdFromAccessToken } from "./jwtUserId";
import { authGeneration, resetAuthSession } from "./authSession";
import { persistAuthTokens, readPersistedAuthTokens } from "./authStorage";
import { isPublicAuthApiPath, shouldSkipAuthRefreshOn401 } from "./authPublicEndpoints";
import { showForbiddenToastOnce } from "./forbiddenToast";
import i18n from "../i18n";

declare module "axios" {
  interface AxiosRequestConfig {
    authGeneration?: number;
    /** Suppress the global permission-denied toast (optional widgets, etc.). */
    skipForbiddenToast?: boolean;
    /** The caller shows its own error toast: skip every global error toast (FH-252). */
    skipErrorToast?: boolean;
    /** Suppress generic connection/server toasts (auth forms handle errors inline). */
    skipGlobalErrorToast?: boolean;
    /** Non-critical background request: never hard-redirect to /login on fatal auth failure. */
    skipAuthRedirect?: boolean;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    config.authGeneration ??= authGeneration();
    if (config.authGeneration !== authGeneration()) throw new axios.CanceledError("Account changed");
    return config;
  },
  (error) => {
    throw error;
  },
  { synchronous: true },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    if (response.config.authGeneration !== authGeneration()) throw new axios.CanceledError("Account changed");
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (axios.isCancel(error)) return Promise.reject(error);
    if (originalRequest?.authGeneration !== undefined && originalRequest.authGeneration !== authGeneration()) {
      return Promise.reject(new axios.CanceledError("Account changed"));
    }
    const generation = authGeneration();

    // Handle 401 Unauthorized
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !shouldSkipAuthRefreshOn401(originalRequest)
    ) {
      const requestAuth =
        originalRequest.headers?.Authorization ??
        originalRequest.headers?.authorization;
      const currentAuth = api.defaults.headers.common["Authorization"] as
        | string
        | undefined;
      const persistedAccess = readPersistedAuthTokens().accessToken;
      const bearerFromStore = persistedAccess
        ? `Bearer ${persistedAccess}`
        : undefined;
      const newestAuth = currentAuth ?? bearerFromStore;

      if (requestAuth && newestAuth && requestAuth !== newestAuth) {
        const requestUserId = userIdFromAccessToken(
          String(requestAuth).replace(/^Bearer\s+/i, ""),
        );
        const newestUserId = userIdFromAccessToken(
          String(newestAuth).replace(/^Bearer\s+/i, ""),
        );
        if (
          requestUserId !== null &&
          newestUserId !== null &&
          requestUserId !== newestUserId
        ) {
          return Promise.reject(new axios.CanceledError("Account changed"));
        }
        originalRequest.headers["Authorization"] = newestAuth;
        return api(originalRequest);
      }

      originalRequest._retry = true;

      const result = await refreshTokens();
      if (generation !== authGeneration()) {
        return Promise.reject(new axios.CanceledError("Account changed"));
      }

      if (result.ok) {
        api.defaults.headers.common["Authorization"] = `Bearer ${result.access}`;
        originalRequest.headers["Authorization"] = `Bearer ${result.access}`;
        return api(originalRequest);
      }

      if (result.fatal) {
        resetAuthSession();
        persistAuthTokens(null, null);
        delete api.defaults.headers.common["Authorization"];
        notifyAuthCleared();
        if (!originalRequest.skipAuthRedirect) {
          redirectToLoginAfterAuthFailure();
        }
      } else {
        notifyBackendUnavailable();
      }
    }

    // Handle other errors
    const method = error.config?.method?.toUpperCase();
    const skipForbiddenToast = originalRequest?.skipForbiddenToast === true;
    const skipGlobalErrorToast = originalRequest?.skipGlobalErrorToast === true;
    if (originalRequest?.skipErrorToast === true) {
      // FH-252: the feature shows a single, specific toast itself.
    } else if (error.response?.status === 403) {
      const authPublicRequest = isPublicAuthApiPath(originalRequest);
      if (
        !skipForbiddenToast &&
        !skipGlobalErrorToast &&
        !authPublicRequest &&
        method &&
        method !== "GET"
      ) {
        showForbiddenToastOnce(error.response?.data);
      }
    } else if (!skipGlobalErrorToast) {
      if (error.response?.status === 404 && method !== "GET") {
        // Only toast 404 on mutations (not GET - those are handled per-query)
        toast.error(i18n.t("errors.notFound"));
      } else if (error.response?.status >= 500) {
        toast.error(i18n.t("errors.server"));
      } else if (!error.response) {
        toast.error(i18n.t("errors.connection"));
      }
    }

    return Promise.reject(error);
  },
);

// API helpers
export const apiHelpers = {
  // Documents
  documents: {
    list: (params?: Record<string, unknown>) => api.get("/documents/", { params }),
    get: (id: number) => api.get(`/documents/${id}/`),
    file: (id: number | string) =>
      api.get(`/documents/${id}/file/`, { responseType: "blob" }),
    create: (data: FormData) =>
      api.post("/documents/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    update: (id: number, data: Record<string, unknown>) => api.patch(`/documents/${id}/`, data),
    delete: (id: number) => api.delete(`/documents/${id}/`),
    reprocess: (id: number) => api.post(`/documents/${id}/reprocess_ocr/`),
    approveSuggestedTags: (id: number, tags: string[], labelOverrides?: Record<string, string>) =>
      api.post(`/documents/${id}/approve_suggested_tags/`, { tags, ...(labelOverrides ? { label_overrides: labelOverrides } : {}) }),
    rejectSuggestedTags: (id: number, tags: string[]) =>
      api.post(`/documents/${id}/reject_suggested_tags/`, { tags }),
    retitle: (id: number) =>
      api.post(`/documents/${id}/retitle/`),
    bulkRetitle: (ids?: number[]) =>
      api.post('/documents/bulk_retitle/', ids?.length ? { ids } : {}),
    scannerInbox: () => api.get("/documents/scanner-inbox/"),
    updateScannerInbox: (hostPath: string) =>
      api.patch("/documents/scanner-inbox/", { host_path: hostPath }),
    triggerScan: () => api.post("/documents/scanner-inbox/scan/"),
    queueStatus: () => api.get("/documents/queue-status/"),
    requestAutoLabel: (id: number) =>
      api.post(`/documents/${id}/request_auto_label/`),
    bulkRequestAutoLabel: (ids: number[]) =>
      api.post("/documents/bulk_request_auto_label/", { ids }),
    archive: (id: number) =>
      api.patch(`/documents/${id}/`, { is_archived: true }),
    unarchive: (id: number) =>
      api.patch(`/documents/${id}/`, { is_archived: false }),
    tags: () => api.get("/documents/tags/"),
    search: (params?: Record<string, unknown>) =>
      api.get("/documents/search/", { params }),
    semanticSearch: (params: { q: string; limit?: number }) =>
      api.get("/documents/semantic-search/", { params }),
    // Aliases for compatibility
    getAll: (params?: Record<string, unknown>) =>
      api.get("/documents/", { params }),
    getCategories: () => api.get("/documents/categories/"),
    upload: (data: FormData) =>
      api.post("/documents/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
  },

  // Properties - main interface
  properties: {
    list: () => api.get("/properties/buildings/"),
    get: (id: number) => api.get(`/properties/buildings/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/properties/buildings/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/buildings/${id}/`, data),
    // Aliases for compatibility
    getBuildings: () => api.get("/properties/buildings/"),
    getApartments: (params?: ApartmentListParams) =>
      api.get("/properties/apartments/", { params }),
    getTenants: (params?: Record<string, unknown>) =>
      api.get("/properties/tenants/", { params }),
    deleteTenant: (id: number) => api.delete(`/properties/tenants/${id}/`),
    archiveApartment: (id: number) =>
      api.patch(`/properties/apartments/${id}/`, { is_archived: true }),
    unarchiveApartment: (id: number) =>
      api.patch(`/properties/apartments/${id}/`, { is_archived: false }),
    duplicateApartment: (id: number) =>
      api.post(`/properties/apartments/${id}/duplicate/`),
  },

  // Structured utility-account migration dashboard
  utilityAccounts: {
    migrationDashboard: (params?: Record<string, unknown>) =>
      api.get("/properties/utility-accounts/migration-dashboard/", { params }),
    get: (id: number, params?: Record<string, unknown>) =>
      api.get(`/properties/utility-accounts/${id}/`, { params }),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/utility-accounts/${id}/`, data),
    bulkUpsert: (data: Record<string, unknown>) =>
      api.post("/properties/utility-accounts/bulk-upsert/", data),
  },

  // Buildings
  buildings: {
    list: () => api.get("/properties/buildings/"),
    get: (id: number) => api.get(`/properties/buildings/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/properties/buildings/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/buildings/${id}/`, data),
    delete: (id: number) => api.delete(`/properties/buildings/${id}/`),
  },

  apartments: {
    list: (params?: ApartmentListParams) =>
      api.get("/properties/apartments/", { params }),
    get: (id: number) => api.get(`/properties/apartments/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/properties/apartments/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/apartments/${id}/`, data),
    delete: (id: number) => api.delete(`/properties/apartments/${id}/`),
    archive: (id: number) => api.patch(`/properties/apartments/${id}/`, { is_archived: true }),
    unarchive: (id: number) => api.patch(`/properties/apartments/${id}/`, { is_archived: false }),
    dashboard: () => api.get("/properties/apartments/dashboard/"),
  },

  tenants: {
    list: (params?: Record<string, unknown>) =>
      api.get("/properties/tenants/", { params }),
    get: (id: number) => api.get(`/properties/tenants/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/properties/tenants/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/tenants/${id}/`, data),
    delete: (id: number) => api.delete(`/properties/tenants/${id}/`),
  },

  contracts: {
    list: (params?: Record<string, unknown>) =>
      api.get("/properties/contracts/", { params }),
    get: (id: number) => api.get(`/properties/contracts/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/properties/contracts/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/contracts/${id}/`, data),
  },

  identities: {
    list: (params?: Record<string, unknown>) =>
      api.get("/properties/identities/", { params }),
    get: (id: number) => api.get(`/properties/identities/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/properties/identities/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/identities/${id}/`, data),
    delete: (id: number) => api.delete(`/properties/identities/${id}/`),
    addRole: (id: number, data: Record<string, unknown>) =>
      api.post(`/properties/identities/${id}/add_role/`, data),
    tenants: () => api.get("/properties/identities/tenants/"),
  },

  identityRoles: {
    list: (params?: Record<string, unknown>) =>
      api.get("/properties/identity-roles/", { params }),
    create: (data: Record<string, unknown>) => api.post("/properties/identity-roles/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/properties/identity-roles/${id}/`, data),
    delete: (id: number) => api.delete(`/properties/identity-roles/${id}/`),
    paymentSummary: (id: number, includeWarranty?: boolean) =>
      api.get(`/properties/identity-roles/${id}/payment_summary/`, {
        params: includeWarranty ? { include_warranty: 'true' } : undefined,
      }),
    recordPayment: (id: number, data: Record<string, unknown>) =>
      api.post(`/properties/identity-roles/${id}/record_payment/`, data),
  },

  // Entity links (AI + manual relationships)
  entityLinks: {
    list: (params?: Record<string, unknown>) =>
      api.get("/links/", { params }),
    get: (id: number) => api.get(`/links/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/links/", data),
    update: (id: number, data: Record<string, unknown>) => api.patch(`/links/${id}/`, data),
    delete: (id: number) => api.delete(`/links/${id}/`),
    accept: (id: number) => api.post(`/links/${id}/accept/`),
    reject: (id: number) => api.post(`/links/${id}/reject/`),
    forEntity: (entityType: string, entityId: number) =>
      api.get(`/links/for/${entityType}/${entityId}/`),
    recommend: (entityType: string, entityId: number) =>
      api.post(`/links/recommend/${entityType}/${entityId}/`),
  },

  accessRequests: {
    list: (params?: Record<string, unknown>) => api.get("/access-requests/", { params }),
    modules: () =>
      api.get("/access-requests/modules/", { skipAuthRedirect: true }),
    create: (data: Record<string, unknown>) => api.post("/access-requests/", data),
    cancel: (id: number) => api.post(`/access-requests/${id}/cancel/`),
    approve: (id: number) => api.post(`/access-requests/${id}/approve/`),
    deny: (id: number) => api.post(`/access-requests/${id}/deny/`),
  },

  // Payments
  payments: {
    list: (params?: Record<string, unknown>) => api.get("/payments/payments/", { params }),
    summary: (params?: Record<string, unknown>) =>
      api.get("/payments/payments/summary/", { params }),
    get: (id: number) => api.get(`/payments/payments/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/payments/payments/", data),
    markPaid: (id: number, data: Record<string, unknown>) =>
      api.post(`/payments/payments/${id}/mark_paid/`, data),
    sendReminder: (id: number) =>
      api.post(`/payments/payments/${id}/send_reminder/`),
    generateMonthly: (data: Record<string, unknown>) =>
      api.post("/payments/payments/generate_monthly/", data),
    delete: (id: number) => api.delete(`/payments/payments/${id}/`),
    // Aliases for compatibility
    getAll: (params?: Record<string, unknown>) => api.get("/payments/payments/", { params }),
    update: (id: number, data: Record<string, unknown>) => api.patch(`/payments/payments/${id}/`, data),
  },

  // HOA Charges
  hoaCharges: {
    list: (params?: Record<string, unknown>) => api.get("/payments/hoa-charges/", { params }),
    get: (id: number) => api.get(`/payments/hoa-charges/${id}/`),
    markPaid: (id: number, data?: Record<string, unknown>) =>
      api.post(`/payments/hoa-charges/${id}/mark_paid/`, data || {}),
    summary: (params?: Record<string, unknown>) =>
      api.get("/payments/hoa-charges/summary/", { params }),
    byApartment: (buildingId: number) =>
      api.get("/payments/hoa-charges/by_apartment/", { params: { building_id: buildingId } }),
  },

  // Budget - main interface
  budget: {
    list: (params?: Record<string, unknown>) =>
      api.get("/budget/transactions/", { params }),
    get: (id: number) => api.get(`/budget/transactions/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/budget/transactions/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/budget/transactions/${id}/`, data),
    delete: (id: number) => api.delete(`/budget/transactions/${id}/`),
    summary: (params?: Record<string, unknown>) =>
      api.get("/budget/summary/", { params }),
    importCSV: (data: FormData) =>
      api.post("/budget/imports/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    // Aliases for compatibility
    getTransactions: (params?: Record<string, unknown>) =>
      api.get("/budget/transactions/", { params }),
    getCategories: () => api.get("/budget/categories/"),
    getAccounts: () => api.get("/budget/accounts/"),
    // Budget limits
    getLimits: () => api.get("/budget/limits/"),
    createLimit: (data: Record<string, unknown>) => api.post("/budget/limits/", data),
    updateLimit: (id: number, data: Record<string, unknown>) =>
      api.patch(`/budget/limits/${id}/`, data),
    deleteLimit: (id: number) => api.delete(`/budget/limits/${id}/`),
    getLimitProgress: (params?: Record<string, unknown>) =>
      api.get("/budget/limits/progress/", { params }),
  },

  // Budget - transactions
  transactions: {
    list: (params?: Record<string, unknown>) =>
      api.get("/budget/transactions/", { params }),
    get: (id: number) => api.get(`/budget/transactions/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/budget/transactions/", data),
    update: (id: number, data: Record<string, unknown>) =>
      api.patch(`/budget/transactions/${id}/`, data),
    delete: (id: number) => api.delete(`/budget/transactions/${id}/`),
    importCSV: (data: FormData) =>
      api.post("/budget/imports/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
  },

  summary: {
    get: (params?: Record<string, unknown>) =>
      api.get("/budget/summary/", { params }),
  },

  // Families
  families: {
    listMembers: (params?: Record<string, unknown>) =>
      api.get("/families/members/", { params }),
    getMember: (id: number) => api.get(`/families/members/${id}/`),
    createMember: (data: Record<string, unknown>) => api.post("/families/members/", data),
    updateMember: (id: number, data: Record<string, unknown>) =>
      api.patch(`/families/members/${id}/`, data),
    deleteMember: (id: number) => api.delete(`/families/members/${id}/`),
    tenantSuggestions: () => api.get("/families/members/tenant_suggestions/"),
    listRelations: () => api.get("/families/relations/"),
    createRelation: (data: Record<string, unknown>) =>
      api.post("/families/relations/", data),
    deleteRelation: (id: number) => api.delete(`/families/relations/${id}/`),
  },

  // Tasks
  tasks: {
    list: (params?: Record<string, unknown>) => api.get("/tasks/", { params }),
    get: (id: number) => api.get(`/tasks/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/tasks/", data),
    update: (id: number, data: Record<string, unknown>) => api.patch(`/tasks/${id}/`, data),
    delete: (id: number) => api.delete(`/tasks/${id}/`),
    updateStatus: (id: number, status: string) =>
      api.post(`/tasks/${id}/update_status/`, { status }),
    getKanban: (params?: Record<string, unknown>) => api.get("/tasks/kanban/", { params }),
    move: (id: number, data: { status: string; order: number }) =>
      api.post(`/tasks/${id}/move/`, data),
    addComment: (id: number, content: string) =>
      api.post(`/tasks/${id}/add_comment/`, { content }),
    addChecklistItem: (id: number, title: string) =>
      api.post(`/tasks/${id}/add_checklist_item/`, { title }),
    toggleChecklistItem: (id: number) => api.post(`/tasks/checklist/${id}/toggle/`),
    // Aliases for compatibility
    getProjects: () => api.get("/tasks/projects/"),
    getTasks: (params?: Record<string, unknown>) => api.get("/tasks/", { params }),
    updateTask: (id: number, data: Record<string, unknown>) => api.patch(`/tasks/${id}/`, data),
    createTask: (data: Record<string, unknown>) => api.post("/tasks/", data),
    createProject: (data: Record<string, unknown>) => api.post("/tasks/projects/", data),
  },

  // Notifications
  notifications: {
    list: () => api.get("/reminders/notifications/"),
    markRead: (id: number) =>
      api.post(`/reminders/notifications/${id}/mark_read/`),
    markAllRead: () => api.post("/reminders/notifications/mark_all_read/"),
    unreadCount: () => api.get("/reminders/notifications/unread_count/"),
  },

  // Users
  users: {
    homeDashboard: () => api.get("/users/home-dashboard/"),
    me: () => api.get("/users/me/"),
    updateMe: (data: Record<string, unknown>) => api.patch("/users/update_me/", data),
    uploadAvatar: (file: File) => {
      const formData = new FormData();
      formData.append("avatar", file);
      return api.post("/users/upload_avatar/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    deleteAvatar: () => api.delete("/users/delete_avatar/"),
    list: () => api.get("/users/"),
    create: (data: Record<string, unknown>) => api.post("/users/", data),
    update: (id: number, data: Record<string, unknown>) => api.patch(`/users/${id}/`, data),
    delete: (id: number) => api.delete(`/users/${id}/`),
  },

  // Integrations
  integrations: {
    list: () => api.get("/integrations/"),
  },

  // Document bundles
  bundles: {
    list: (params?: Record<string, unknown>) => api.get("/bundles/", { params }),
    get: (id: number) => api.get(`/bundles/${id}/`),
    create: (data: Record<string, unknown>) => api.post("/bundles/", data),
    update: (id: number, data: Record<string, unknown>) => api.patch(`/bundles/${id}/`, data),
    delete: (id: number) => api.delete(`/bundles/${id}/`),
    tree: () => api.get("/bundles/tree/"),
    documents: (id: number, params?: Record<string, unknown>) =>
      api.get(`/bundles/${id}/documents/`, { params }),
    addDocuments: (id: number, documentIds: number[]) =>
      api.post(`/bundles/${id}/add_documents/`, { document_ids: documentIds }),
    removeDocuments: (id: number, documentIds: number[]) =>
      api.post(`/bundles/${id}/remove_documents/`, { document_ids: documentIds }),
    refreshSmart: (id: number) => api.post(`/bundles/${id}/refresh_smart/`),
    suggest: (documentIds: number[]) =>
      api.post("/bundles/suggest/", { document_ids: documentIds }),
    autoSuggest: () => api.get("/bundles/auto_suggest/"),
  },

  // AI / LLM lifecycle
  ai: {
    status: () => api.get("/ai/llm/status/"),
    models: () => api.get("/ai/llm/models/"),
    setModel: (model: string) => api.post("/ai/llm/model/", { model }),
    start: () => api.post("/ai/llm/start/"),
    stop: () => api.post("/ai/llm/stop/"),
  },

  // CI/CD Runner lifecycle
  ci: {
    status: () => api.get("/ci/runner/status/"),
    start: () => api.post("/ci/runner/start/"),
    stop: () => api.post("/ci/runner/stop/"),
  },

  // API Keys
  apiKeys: {
    list: () => api.get("/users/api-keys/"),
    create: (data: Record<string, unknown>) => api.post("/users/api-keys/", data),
    update: (id: string, data: Record<string, unknown>) =>
      api.patch(`/users/api-keys/${id}/`, data),
    revoke: (id: string) => api.delete(`/users/api-keys/${id}/`),
    catalog: () => api.get("/users/api-keys/catalog/"),
    presets: () => api.get("/users/api-keys/presets/"),
  },

  // Knowledge (FH-KNOWLEDGE second brain)
  knowledge: {
    listNotes: (params?: Record<string, unknown>) =>
      api.get("/knowledge/notes/", { params }),
    getNote: (id: number) => api.get(`/knowledge/notes/${id}/`),
    createNote: (data: Record<string, unknown>) =>
      api.post("/knowledge/notes/", data),
    updateNote: (id: number, data: Record<string, unknown>) =>
      api.patch(`/knowledge/notes/${id}/`, data),
    deleteNote: (id: number) => api.delete(`/knowledge/notes/${id}/`),
    search: (params: Record<string, unknown>) =>
      api.get("/knowledge/search/", { params }),
    getBrainProfile: () => api.get("/knowledge/brain-profile/"),
    patchBrainProfile: (data: Record<string, unknown>) =>
      api.patch("/knowledge/brain-profile/", data),
    listAreas: (params?: Record<string, unknown>) =>
      api.get("/knowledge/areas/", { params }),
  },

  // Backup Portal
  backup: {
    getConfig: () => api.get("/backup/config/"),
    patchConfig: (data: Record<string, unknown>) => api.patch("/backup/config/", data),
    getSnapshots: () => api.get("/backup/snapshots/"),
    certifySnapshot: (id: number) => api.post(`/backup/snapshots/${id}/certify/`),
    uncertifySnapshot: (id: number) => api.post(`/backup/snapshots/${id}/uncertify/`),
    runNow: () => api.post("/backup/run/"),
  },

  // Estate budget planner
  budgetPlanner: {
    getProjection: (months: number) =>
      api.get("/budget/projection/", { params: { occupancy_months: months } }),
    getScenarioTable: () => api.get("/budget/scenario-table/"),
    getBondSchedule: () => api.get("/budget/bond-schedule/"),
    getFiveYearPlan: (occupancy: number, appreciationRate: number) =>
      api.get("/budget/five-year-plan/", {
        params: { occupancy, appreciation_rate: appreciationRate },
      }),
    getMonthlyAdvice: (liquidCash: number) =>
      api.get("/budget/monthly-advice/", { params: { liquid_cash: liquidCash } }),
  },
};

